/**
 * Header.js — Site-wide navigation header.
 *
 * Renders:
 *  - An optional dismissible notification bar (fetched from the admin panel API).
 *  - The main navbar with logo, navigation links, user account control, cart badge,
 *    and a search form.
 *  - A secondary navbar row populated by the dynamic store page menu.
 *
 * Props:
 *  - AdminPanelURL {string}         Base URL for the admin panel API.
 *  - ProductPageJson {Array}        Sorted list of store page menu items.
 *  - HeaderSearchText {string}      Controlled search text from App state.
 *  - setHeaderSearchText {Function} Updates App-level search text.
 *  - Cartqty {number}               Current cart item count.
 *  - CartAmount {object}            Cart total — expects { amount: number }.
 *  - useLoggedIn {boolean}          Whether the user is authenticated.
 *  - redirectTocart {Function}      Opens the cart sidebar.
 *  - ShowAboutPagePopup {Function}  Opens the About Us modal.
 *  - ShowLoginPopup {Function}      Opens the login modal.
 *  - ShowLoginPopup_ {Function}     Redirects to profile if logged in, else opens login.
 *  - ShowCreateAccountPopup {Function} Opens the create-account modal.
 *  - ShowSignUpPopup {Function}     Opens the sign-up modal.
 *  - offerBar {boolean}             Whether the offer/notification bar is visible.
 *  - setofferBar {Function}         Toggles the offer bar state.
 */

// ─── External libraries ───────────────────────────────────────────────────────
import React, { useState, useEffect, useRef, useCallback  } from 'react';
import { Link, useLocation, useHistory } from 'react-router-dom';
import axios from 'axios';
import queryString from 'query-string';
import {
  Navbar,
  Container,
  Nav,
  Form,
  FormControl,
  Button,
  Badge,
  ListGroup,
} from 'react-bootstrap';

// ─── Internal — icons & images ────────────────────────────────────────────────
import {SearchIcon} from "../../icons";
import ProgIcon from "../../images/program.svg";
import CartImg from "../../images/cart.svg";
import LeoCussenLogoHeader from "../../images/header-logo-svg.svg";
import LeoCussenIcon from "../../images/home-vector.svg";
import ProfileIcon from "../../images/Profile.svg";

/**
 * Formats a number as a 2-decimal-place string with comma thousands separators.
 *
 * @param {number|string} x
 * @returns {string} e.g. "1,234.56"
 */
const numberWithCommas = (x) => {
  return parseFloat(x).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

/**
 * Formats the notification-bar content for display.
 *
 * The admin panel's `api/notification` endpoint strips HTML tags, so formatted
 * content (bold headline + "Sign up" link) arrives as plain text. This restores
 * the intended styling on the frontend:
 *   • If the API returns real HTML (any tag present) — e.g. once the backend is
 *     fixed — it is rendered untouched.
 *   • Otherwise the plain sale text is reformatted: the headline up to and
 *     including the first "!" is bolded, and the "Sign up" call-to-action is
 *     turned into the claret `.top_bar_link`.
 */
const formatBarContent = (text) => {
  if (!text) return "";
  // Already HTML (backend fixed) → use as-is.
  if (/<[a-z][\s\S]*>/i.test(text)) return text;

  let html = text
    // Linkify the "Sign up" CTA (first occurrence only).
    .replace(/Sign up/i, '<a class="top_bar_link">$&</a>')
    // Bold the headline up to and including the first exclamation mark.
    .replace(/^(.*?!)/, '<strong>$1</strong>');
  return `<p>${html}</p>`;
};

// ─── Main component ───────────────────────────────────────────────────────────

/**
 * Header — site navigation bar.
 * See file-level JSDoc for full props description.
 */
export const Header = (props) => {

  const location = useLocation();
  const queryParam = queryString.parse(location.search);
  let history = useHistory();
  // Preserve Google Ads click ID across navigation so it carries to the cart.
  const GAurl = queryParam['gclid'] !== undefined ? '?gclid=' + queryParam['gclid'] : '';
  const [search_text, set_search_text] = useState();
  const [DisableBarStatus, setDisableBarStatus] = useState(false);
  const [TopBarText, setTopBarText] = useState();
  const [navExpanded, setNavExpanded] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const navRef = useRef(null);

  /** Reads the dismiss state from localStorage so the bar stays hidden after a page reload. */
  const [DisableBar, setDisableBar] = useState(() => {
    const offerBar = localStorage.getItem("DisableBar");
    return offerBar !== null && (offerBar === false || offerBar === "false")
      ? false
      : true;
  });

  /** Syncs the search input when the URL query param ?s= changes (e.g. back/forward navigation). */
  useEffect(() => {
    const queryParam = queryString.parse(location.search);
    let search = queryParam['s'];
    if (search !== undefined) {
      set_search_text(search);
    }
  }, [location.search]);

  /** Syncs the search input when App-level HeaderSearchText prop changes. */
  useEffect(() => {
    set_search_text(props.HeaderSearchText);
    const queryParam = queryString.parse(location.search);
    let search = queryParam['s'];
    if (search !== undefined) {
      set_search_text(search);
    }
  }, [props.HeaderSearchText,location.search]);

  /**
   * Handles the search form submission.
   * Updates App state and navigates to the /search route with the query string.
   *
   * @param {Event} event - Form submit event.
   */
  const main_search_form = (event) => {
    event.preventDefault();
    props.setHeaderSearchText(search_text);
    history.push("/search?s=" + search_text);
  };

  /**
   * Dismisses the notification bar and persists the choice to localStorage
   * so it stays hidden on subsequent page loads.
   */
  const CloseOfferBAR = () => {
    setDisableBar(false);
    localStorage.setItem("DisableBar", false);
  };

  /**
   * Fetches the notification bar content and enabled status from the admin panel API.
   * Runs once on mount.
   */
  const fetchData = useCallback(async () => {
    try {
      const admin_panel_api_URL = props.AdminPanelURL + 'api/notification';

      const admin_panel_response = await axios({
        method: 'get',
        url: admin_panel_api_URL,
        headers: {
            'X-API-KEY': props.API_KEY   // ← Add this
        }
      });

      if (admin_panel_response.status === 200) {
        var data = admin_panel_response.data;
        if (data.length !== 0) {
          setTopBarText(data.notification_content);
          setDisableBarStatus(data.enable_notification);
        }
      }
    } catch (error) {
      console.log(true);
    }
  },[props.AdminPanelURL]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /** Close mobile nav when user clicks outside the navbar element */
  useEffect(() => {
    if (!navExpanded) return;
    const handleOutsideClick = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setNavExpanded(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [navExpanded]);

  /** Lock body scroll while mobile nav is open */
  useEffect(() => {
    document.body.style.overflow = navExpanded ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [navExpanded]);

  /** Make header sticky on mobile after 200px scroll */
  useEffect(() => {
    const handleScroll = () => setIsSticky(window.scrollY >= 200);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      {/* Notification bar — only shown when enabled by admin AND not dismissed by user */}
      {DisableBarStatus ? DisableBar ?
        <div className="top_bar_wrapper">
          <div className="top_bar_inner">
            <div className="top_bar_contnet">
              <div onClick={() => props.ShowSignUpPopup()} dangerouslySetInnerHTML={{ __html: formatBarContent(TopBarText) }} />
            </div>
          </div>
          <button onClick={() => CloseOfferBAR()} className="top_bar_close" aria-label="Close">&#x2715;</button>
        </div>
        : null : null}

      <Navbar ref={navRef} bg="white" expand="lg" className={`flex-wrap py-0 lc-header-card${isSticky ? ' lc-header-sticky' : ''}`} expanded={navExpanded} onToggle={setNavExpanded}>
        <Container className="lc-topnav">
          <Navbar.Brand href="https://www.leocussen.edu.au/" target="_blank"><img src={LeoCussenLogoHeader} alt="Leo Cussen Centre for Law" className="lc-header-logo" /></Navbar.Brand>
          <div className="lc-mobile-right d-flex d-lg-none align-items-center">
            {props.useLoggedIn && (
              <button onClick={() => props.ShowLoginPopup_()} className="nav-link account_link mobile-account-link">
                <img src={ProfileIcon} alt="My Profile" className="profile-icon" />
              </button>
            )}
            <div className="cart_link" onClick={() => props.redirectTocart()}>
              <img src={CartImg} alt="Cart" className="cart-icon-img" />
              <Badge variant="danger" bg="danger">{props.Cartqty}</Badge>
            </div>
          </div>
          <Navbar.Toggle aria-controls="basic-navbar-nav">
            <span className="lc-toggler-bar"></span>
            <span className="lc-toggler-bar"></span>
          </Navbar.Toggle>
          <div className="navbar_right d-flex flex-wrap align-items-center">
            <Nav className="ms-lg-auto d-flex flex-row align-items-center justify-content-lg-start">
              <Link to={"/" + GAurl} className="home_link d-lg-none"><img src={LeoCussenIcon} alt="" className='d-block' /> Store Home</Link>
              <a className="nav-link d-none" onClick={props.ShowAboutPagePopup}>About us</a>
              <Link to="/all-courses?view=calendar" className="nav-link d-none d-lg-flex align-items-center"><img src={ProgIcon} alt="" className="prog-icon" />Program</Link>
              {!props.useLoggedIn ? (
                <>
                  <a onClick={() => props.ShowCreateAccountPopup()} className="btn btn-secondary">
                    {props.useLoggedIn ? 'My Account' : 'Sign up'}
                  </a>
                  <a onClick={() => props.ShowLoginPopup_()} className="btn btn-secondary-outline">
                    {props.useLoggedIn ? 'My Account' : 'Log in'}
                  </a>
                </>
              ) : (
                <a onClick={() => props.ShowLoginPopup_()} className="nav-link account_link"><img src={ProfileIcon} alt="My Profile" className="profile-icon" /></a>
              )}
              <a onClick={() => props.redirectTocart()} className="nav-link cart_link d-none d-lg-flex align-items-center">
                <img src={CartImg} alt="Cart" className="cart-icon-img" />
                <Badge variant="danger" bg="danger">{props.Cartqty}</Badge>
                {props.CartAmount.amount > 0 ? <span className="cart_price">${numberWithCommas(props.CartAmount.amount)}</span> : ''}
              </a>
            </Nav>
            <Form onSubmit={main_search_form} className="d-flex search_bar align-items-center position-relative d-none">
              <FormControl
                type="search"
                placeholder="Type and enter to search"
                className="search_input"
                onChange={(e) => set_search_text(e.target.value)}
                value={search_text}
                aria-label="Search"
              />
              <Button type="submit" variant="search"><SearchIcon /></Button>
            </Form>
          </div>
        </Container>

        {/* Secondary nav row — dynamic menu items from ProductPageJson */}
        <Container fluid className="bg-white lc-subnav-card">
          <Navbar.Collapse id="basic-navbar-nav">
            <Nav className="mx-auto" as="ul">
              <Link to={"/" + GAurl} className={`home_link d-none d-lg-flex align-items-center${history.location.pathname === '/' ? ' active' : ''}`}><img src={LeoCussenIcon} alt="" /> Store Home</Link>
              <ListGroup.Item
                as="li"
                className={history.location.pathname.replace('/', '') === "all-courses" ? 'active' : ''}
              >
                <Link to={"/all-courses" + GAurl} className="nav-link" onClick={() => setNavExpanded(false)}>All Programs</Link>
              </ListGroup.Item>
              {props.ProductPageJson.map((menu, index) => {
                const menuname = menu.Name;
                const slug = menuname.split(' ').join('-').toLowerCase();
                let active_class = '';
                if (slug === history.location.pathname.replace('/', '')) {
                  active_class = 'active';
                }
                return (
                  <ListGroup.Item eventKey={index} key={index} as="li" className={active_class}>
                    <Link to={"/" + slug + GAurl} className="nav-link" onClick={() => setNavExpanded(false)}>{menu.Name}</Link>
                  </ListGroup.Item>
                );
              })}
            </Nav>
            {/* Mobile-only: buttons strip at bottom of open menu (Figma 98:59320) */}
            <div className="mobile-nav-strip d-lg-none">
              <Link to={"/" + GAurl} className="home_link" onClick={() => setNavExpanded(false)}><img src={LeoCussenIcon} alt="" /> Store Home</Link>
              {!props.useLoggedIn ? (
                <>
                  <a onClick={() => { props.ShowCreateAccountPopup(); setNavExpanded(false); }} className="btn btn-secondary">Sign up</a>
                  <a onClick={() => { props.ShowLoginPopup_(); setNavExpanded(false); }} className="btn btn-secondary-outline">Log in</a>
                </>
              ) : null}
            </div>
          </Navbar.Collapse>
        </Container>

      </Navbar>
    </>
  );
};
